'use strict';

(function (global) {
  var DAY_MS = 24 * 60 * 60 * 1000;
  var DISMISSED_UNTIL_KEY = 'hj_welcome_popup_dismissed_until';
  var SUBSCRIBED_KEY = 'hj_welcome_popup_subscribed';
  var normalAutoOpenReserved = false;
  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function createExpiryValue(now, days) {
    var timestamp = Number(now);
    var duration = Number(days);

    if (!Number.isFinite(timestamp)) return '';
    if (!Number.isFinite(duration) || duration < 0) duration = 0;

    return String(timestamp + duration * DAY_MS);
  }

  function isUnexpired(raw, now) {
    var expiry = Number(raw);
    var timestamp = Number(now);

    return Number.isFinite(expiry) && Number.isFinite(timestamp) && expiry > timestamp;
  }

  function getFocusableElements(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    return Array.prototype.slice.call(root.querySelectorAll(focusableSelector)).filter(function (element) {
      return !element.disabled && (!element.getAttribute || element.getAttribute('aria-hidden') !== 'true');
    });
  }

  function restoreCopyLabel(button, label) {
    if (button && label) button.textContent = label;
  }

  function getStorage() {
    try {
      return global.localStorage;
    } catch (error) {
      return null;
    }
  }

  function safeStorageGet(key) {
    var storage = getStorage();
    if (!storage) return null;

    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    var storage = getStorage();
    if (!storage) return false;

    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function hasTrueValue(value) {
    return value === 'true';
  }

  function copyWithFallback(value) {
    if (!global.document || !global.document.body) return false;

    var previouslyFocused = global.document.activeElement;
    var textarea = global.document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    global.document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch (error) {
      return false;
    } finally {
      textarea.remove();
      if (previouslyFocused && previouslyFocused.isConnected && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    }
  }

  function copyText(value) {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      return navigator.clipboard.writeText(value).then(function () {
        return true;
      }).catch(function () {
        return copyWithFallback(value);
      });
    }

    return Promise.resolve(copyWithFallback(value));
  }

  function initializePopup(root) {
    if (!root || root.getAttribute('data-hj-popup-initialized') === 'true') return;
    root.setAttribute('data-hj-popup-initialized', 'true');

    var overlay = root.querySelector('[data-hj-popup-overlay]');
    var dialog = root.querySelector('[data-hj-popup-dialog]');
    var closeButton = root.querySelector('[data-hj-popup-close]');
    var emailInput = root.querySelector('[data-hj-popup-email]');
    var code = root.querySelector('[data-hj-popup-code]');
    var copyButton = root.querySelector('[data-hj-popup-copy]');
    var copyStatus = root.querySelector('[data-hj-popup-copy-status]');
    var isSuccess = hasTrueValue(root.getAttribute('data-success'));
    var hasErrors = hasTrueValue(root.getAttribute('data-has-errors'));
    var isDesignMode = hasTrueValue(root.getAttribute('data-design-mode'));
    var dismissDays = Number(root.getAttribute('data-dismiss-days'));
    var delay = Number(root.getAttribute('data-delay-ms'));
    var pendingTimer = null;
    var copyTimer = null;
    var copyLabel = '';
    var previouslyFocused = null;
    var isOpen = false;

    if (!dialog) return;
    if (!Number.isFinite(dismissDays) || dismissDays < 0) dismissDays = 0;
    if (!Number.isFinite(delay) || delay < 0) delay = 0;

    function unlockBody() {
      if (!global.document.querySelector('[data-hj-welcome-popup].is-open')) {
        global.document.body.classList.remove('hj-popup-open');
      }
    }

    function openPopup() {
      if (isOpen) return;

      isOpen = true;
      previouslyFocused = global.document.activeElement;
      root.hidden = false;
      root.classList.add('is-open');
      global.document.body.classList.add('hj-popup-open');

      if (global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        root.classList.add('hj-popup-reduced-motion');
      }

      (emailInput || dialog).focus();
    }

    function closePopup(customerInitiated) {
      if (pendingTimer) {
        global.clearTimeout(pendingTimer);
        pendingTimer = null;
      }
      if (copyTimer) {
        restoreCopyLabel(copyButton, copyLabel);
        global.clearTimeout(copyTimer);
        copyTimer = null;
      }

      if (!isOpen) return;

      isOpen = false;
      root.classList.remove('is-open');
      root.hidden = true;
      unlockBody();

      if (customerInitiated) {
        safeStorageSet(DISMISSED_UNTIL_KEY, createExpiryValue(Date.now(), dismissDays));
      }

      if (previouslyFocused && previouslyFocused.isConnected && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    }

    function trapFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup(true);
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = getFocusableElements(dialog);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = global.document.activeElement;

      if (event.shiftKey && (active === dialog || active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === dialog || active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    function announceCopy(message) {
      if (copyStatus) copyStatus.textContent = message;
    }

    function handleCopy() {
      var value = code && code.textContent ? code.textContent.trim() : '';
      if (!value) {
        announceCopy('Copy failed. Select the code and copy it manually.');
        return;
      }

      copyText(value).then(function (didCopy) {
        if (!didCopy) {
          announceCopy('Copy failed. Select the code and copy it manually.');
          return;
        }

        var copiedLabel = copyButton.getAttribute('data-copied-label') || copyButton.textContent;
        copyLabel = copyButton.getAttribute('data-copy-label') || copyButton.textContent;
        copyButton.textContent = copiedLabel;
        announceCopy('Discount code copied.');
        global.clearTimeout(copyTimer);
        copyTimer = global.setTimeout(function () {
          restoreCopyLabel(copyButton, copyLabel);
          copyTimer = null;
        }, 2000);
      });
    }

    function hideFailedImage(event) {
      var image = event.currentTarget;
      var media = image.closest('.hj-welcome-popup__media') || image.closest('.hj-welcome-popup__image');
      if (media) media.hidden = true;
      dialog.classList.add('hj-welcome-popup__dialog--no-image');
    }

    if (closeButton) closeButton.addEventListener('click', function () { closePopup(true); });
    if (overlay) overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closePopup(true);
    });
    dialog.addEventListener('keydown', trapFocus);
    if (copyButton) copyButton.addEventListener('click', handleCopy);
    root.querySelectorAll('img').forEach(function (image) {
      image.addEventListener('error', hideFailedImage);
      if (image.complete && image.naturalWidth === 0) {
        hideFailedImage({ currentTarget: image });
      }
    });

    if (isSuccess) safeStorageSet(SUBSCRIBED_KEY, 'true');

    if (isSuccess || hasErrors || isDesignMode) {
      openPopup();
      return;
    }

    if (normalAutoOpenReserved || safeStorageGet(SUBSCRIBED_KEY) === 'true' || isUnexpired(safeStorageGet(DISMISSED_UNTIL_KEY), Date.now())) {
      return;
    }

    normalAutoOpenReserved = true;
    pendingTimer = global.setTimeout(openPopup, delay);
  }

  function initializeWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches('[data-hj-welcome-popup]')) initializePopup(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('[data-hj-welcome-popup]').forEach(initializePopup);
    }
  }

  function initializeRuntime() {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () {
        initializeWithin(global.document);
      }, { once: true });
    } else {
      initializeWithin(global.document);
    }

    global.document.addEventListener('shopify:section:load', function (event) {
      initializeWithin(event.target);
    });
  }

  var helpers = {
    createExpiryValue: createExpiryValue,
    isUnexpired: isUnexpired,
    getFocusableElements: getFocusableElements,
    restoreCopyLabel: restoreCopyLabel
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = helpers;
  } else if (global && global.document) {
    initializeRuntime();
  }
})(typeof window !== 'undefined' ? window : globalThis);
